import { useQuery } from "../data/useQuery.js";
import { useBoundQuery, FillPrompt } from "../data/DwSql.js";
import { ErrorBadge } from "./ErrorBadge.js";
import {
  formatValue,
  formatForName,
  parseFormat,
  compactLabel,
} from "./format.js";
import { yDomain, baselineOf } from "./scale.js";
import type { QueryResult } from "../../shared/types.js";
import type { VizProps } from "./registry.js";

// Dependency-free SVG grouped bar chart. The first result column (or `x=`) is
// the category; the remaining numeric columns (or `series="a,b"`) are the bars.
const PALETTE = ["#7c5cff", "#22d3ee", "#22c55e", "#f59e0b", "#ef4444", "#a78bfa"];

export function BarChartViz({ source, attrs }: VizProps) {
  const { sql, values, path, status, missing } = useBoundQuery(source.trim());
  const q = useQuery(status === "ready" ? sql : null, {
    // Explicit attr overrides the query's @maxAge pragma; absent → pragma →
    // forever (useQuery sends maxAgeDefault).
    ttl: attrs.maxAge ?? attrs.ttl,
    values,
    path,
  });
  return (
    <div className="dw-embed dw-embed-chart">
      {status === "blocked" && <FillPrompt names={missing} />}
      {status === "ready" && q.status === "loading" && (
        <div className="dw-loading">Running…</div>
      )}
      {status === "ready" && q.status === "error" && (
        <ErrorBadge message={q.error.message} />
      )}
      {status === "ready" && q.status === "ready" && (
        <BarChart result={q.data} attrs={attrs} />
      )}
    </div>
  );
}

function BarChart({
  result,
  attrs,
}: {
  result: QueryResult;
  attrs: Record<string, string>;
}) {
  const colNames = result.columns.map((c) => c.name);
  const xKey = attrs.x && colNames.includes(attrs.x) ? attrs.x : colNames[0];
  const xi = colNames.indexOf(xKey);

  const seriesNames = (
    attrs.series ? attrs.series.split(",").map((s) => s.trim()) : colNames
  ).filter((name) => name !== xKey && colNames.includes(name));
  if (seriesNames.length === 0) {
    return <div className="dw-empty">No numeric series to plot.</div>;
  }
  const fmtOf = parseFormat(attrs.format);

  const cats = result.rows.map((r) => String(r[xi] ?? ""));
  const series = seriesNames.map((name, s) => ({
    name,
    color: PALETTE[s % PALETTE.length],
    fmt: fmtOf(name) ?? formatForName(name),
    vals: result.rows.map((r) => Number(r[colNames.indexOf(name)] ?? 0) || 0),
  }));

  const domain = yDomain(series.flatMap((s) => s.vals), attrs);
  const [yMin, yMax] = domain;
  // Bars grow from zero, or from the nearer edge when ymin= pins it off-screen.
  const base = baselineOf(domain);
  const W = Math.max(640, cats.length * 64);
  const H = Number(attrs.height) || 300;
  const m = { top: 16, right: 16, bottom: 40, left: 56 };
  const plotW = W - m.left - m.right;
  const plotH = H - m.top - m.bottom;
  const y = (v: number) => m.top + plotH - ((v - yMin) / (yMax - yMin)) * plotH;

  const groupW = (plotW / cats.length) * 0.72;
  const barW = groupW / series.length;
  const ticks = [yMin, (yMin + yMax) / 2, yMax];
  // `labels=true` writes each bar's value above it (compact, e.g. "$78K").
  const showLabels = !!attrs.labels && attrs.labels !== "false";

  return (
    <div className="dw-chart">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="none"
        style={{ height: H }}>
        {/* y gridlines + labels */}
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={m.left} x2={W - m.right} y1={y(t)} y2={y(t)}
              stroke="var(--dw-border)" strokeDasharray="3 3" />
            <text x={m.left - 8} y={y(t) + 4} textAnchor="end"
              fontSize="11" fill="var(--dw-muted)">
              {compactLabel(t, series[0].fmt)}
            </text>
          </g>
        ))}
        {/* bars */}
        {cats.map((cat, ci) => {
          const gx = m.left + (ci + 0.5) * (plotW / cats.length) - groupW / 2;
          return (
            <g key={ci}>
              {series.map((s, si) => {
                const v = s.vals[ci];
                const bx = gx + si * barW;
                const by = y(Math.max(base, v));
                const bh = Math.abs(y(v) - y(base));
                return (
                  <g key={si}>
                    <rect x={bx} y={by} width={Math.max(barW - 2, 1)}
                      height={bh} rx={2} fill={s.color}>
                      <title>{`${cat} · ${s.name}: ${formatValue(v, s.fmt)}`}</title>
                    </rect>
                    {showLabels && (
                      <text x={bx + barW / 2} y={by - 4} textAnchor="middle"
                        fontSize="10" fill="currentColor">
                        {compactLabel(v, s.fmt)}
                      </text>
                    )}
                  </g>
                );
              })}
              <text x={m.left + (ci + 0.5) * (plotW / cats.length)}
                y={H - m.bottom + 16} textAnchor="middle" fontSize="11"
                fill="var(--dw-muted)">
                {cat}
              </text>
            </g>
          );
        })}
      </svg>
      {series.length > 1 && (
        <div className="dw-chart-legend">
          {series.map((s) => (
            <span key={s.name} className="dw-legend-item">
              <span className="dw-legend-swatch" style={{ background: s.color }} />
              {s.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
